import React from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Chip,
    LinearProgress,
    Paper,
    Stack,
    Typography,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import { fmtEUR, formatDateDisplay, recurrenceLabel } from "./expenseManagementUtils";

export default function RecurringExpenseSection({
    recurringLoading,
    recurringError,
    recurringExpenses,
    handleOpenRecurringCreate,
    handleOpenRecurringEdit,
    handleDeleteRecurring,
    recurringDeleteId,
}) {
    return (
        <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3.5 }, borderRadius: 3 }}>
            <Stack spacing={3}>
                <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={2}
                    alignItems={{ xs: "flex-start", md: "center" }}
                    justifyContent="space-between"
                >
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                            Wiederkehrende Ausgaben
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: 640 }}>
                            Halte Miet-, Leasing- oder andere regelmäßige Kosten als Vorlage fest. Schreiberlein
                            legt daraus automatisch Ausgaben an.
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddRoundedIcon />}
                        onClick={handleOpenRecurringCreate}
                        sx={{
                            backgroundColor: "orange",
                            color: "#000",
                            fontWeight: 700,
                            "&:hover": { backgroundColor: "#ff9e1a" },
                        }}
                    >
                        Vorlage anlegen
                    </Button>
                </Stack>

                {recurringLoading ? (
                    <Box sx={{ py: 2 }}>
                        <LinearProgress />
                    </Box>
                ) : recurringError ? (
                    <Alert severity="error">{recurringError}</Alert>
                ) : recurringExpenses.length === 0 ? (
                    <Stack spacing={1.5}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Noch keine wiederkehrenden Ausgaben hinterlegt.
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Lege deine erste Vorlage an und behalte wiederkehrende Kosten automatisch im Blick.
                        </Typography>
                        <Button variant="outlined" startIcon={<AddRoundedIcon />} onClick={handleOpenRecurringCreate}>
                            Erste Vorlage erstellen
                        </Button>
                    </Stack>
                ) : (
                    <Grid2 container spacing={2.5}>
                        {recurringExpenses.map((transaction) => {
                            const grossText =
                                transaction?.grossAmount !== null && transaction?.grossAmount !== undefined
                                    ? fmtEUR(transaction.grossAmount)
                                    : "—";
                            const netText =
                                transaction?.netAmount !== null && transaction?.netAmount !== undefined
                                    ? fmtEUR(transaction.netAmount)
                                    : null;
                            const vatText =
                                transaction?.vatAmount !== null && transaction?.vatAmount !== undefined
                                    ? fmtEUR(transaction.vatAmount)
                                    : null;

                            return (
                                <Grid2 key={transaction.id} xs={12} sm={6} md={4}>
                                    <Card
                                        variant="outlined"
                                        sx={{
                                            height: "100%",
                                            display: "flex",
                                            flexDirection: "column",
                                            borderRadius: 3,
                                        }}
                                    >
                                        <CardContent sx={{ flexGrow: 1 }}>
                                            <Stack spacing={1.5}>
                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    justifyContent="space-between"
                                                    alignItems="flex-start"
                                                >
                                                    <Typography variant="subtitle1" fontWeight={700} sx={{ pr: 1 }}>
                                                        {transaction.title || "Unbenannte Vorlage"}
                                                    </Typography>
                                                    <Chip
                                                        size="small"
                                                        label={recurrenceLabel(transaction.recurrencePeriod)}
                                                        color="primary"
                                                        variant="outlined"
                                                    />
                                                </Stack>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <CalendarMonthRoundedIcon fontSize="small" sx={{ opacity: 0.7 }} />
                                                    <Typography variant="body2" color="text.secondary">
                                                        Nächste Ausführung: {formatDateDisplay(transaction.nextExecutionDate)}
                                                    </Typography>
                                                </Stack>
                                                <Typography variant="body2" color="text.secondary">
                                                    Start: {formatDateDisplay(transaction.startDate)}
                                                    {transaction.endDate
                                                        ? ` · Ende: ${formatDateDisplay(transaction.endDate)}`
                                                        : ""}
                                                    {transaction.dayOfMonth
                                                        ? ` · Fälligkeitstag: ${transaction.dayOfMonth}.`
                                                        : ""}
                                                </Typography>
                                                <Typography variant="body2">Betrag brutto: {grossText}</Typography>
                                                {netText && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        Netto: {netText}
                                                    </Typography>
                                                )}
                                                {vatText && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        USt: {vatText}
                                                    </Typography>
                                                )}
                                                {transaction.counterparty && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        Lieferant: {transaction.counterparty}
                                                    </Typography>
                                                )}
                                                {transaction.category && (
                                                    <Chip
                                                        size="small"
                                                        label={transaction.category}
                                                        variant="outlined"
                                                        sx={{ alignSelf: "flex-start" }}
                                                    />
                                                )}
                                                {transaction.invoiceNumberTemplate && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        Belegnr.-Muster: {transaction.invoiceNumberTemplate}
                                                    </Typography>
                                                )}
                                            </Stack>
                                        </CardContent>
                                        <CardActions sx={{ pt: 0, pb: 2, px: 2 }}>
                                            <Button
                                                size="small"
                                                startIcon={<EditRoundedIcon />}
                                                onClick={() => handleOpenRecurringEdit(transaction)}
                                            >
                                                Bearbeiten
                                            </Button>
                                            <Button
                                                size="small"
                                                color="error"
                                                startIcon={<DeleteRoundedIcon />}
                                                onClick={() => handleDeleteRecurring(transaction)}
                                                disabled={recurringDeleteId === transaction.id}
                                            >
                                                Löschen
                                            </Button>
                                        </CardActions>
                                    </Card>
                                </Grid2>
                            );
                        })}
                    </Grid2>
                )}
            </Stack>
        </Paper>
    );
}
